import type { MapPoint } from "../components/DeliveryMap";
import { readArtworkLibrary } from "./sidebar-props";

export const galleryCategories = ["Exterior", "Ambience", "How we prepare"] as const;
export type GalleryCategory = typeof galleryCategories[number];
export type GalleryPhoto = { id: string; imageUrl: string; caption: string; category: GalleryCategory };
export type CafeBranch = { id: string; name: string; address: string; phone: string; email: string; openingHours: string; latitude: number | null; longitude: number | null; pinnedAddress: string };
export const cafeGallerySettingKeys = ["cafe_gallery_photos", "cafe_description", "cafe_description_height", "cafe_branches", "cafe_facebook_url", "cafe_youtube_url", "cafe_instagram_url", "cafe_tiktok_url", "about_us_artwork", "about_us_artwork_placement"] as const;

export const readAboutUsArtworks = readArtworkLibrary;

export function readAboutUsArtwork(settings: Record<string, string>) {
  return {
    artwork: readAboutUsArtworks(settings).find(prop => prop.id === settings.about_us_artwork) ?? null,
    placement: settings.about_us_artwork_placement === "all-over" ? "all-over" as const : "center" as const
  };
}

export function imageAllowed(value: string) {
  return /^https?:\/\//i.test(value) || (value.startsWith("/") && !value.startsWith("//"));
}
export function readGalleryPhotos(value?: string): GalleryPhoto[] {
  try {
    const rows: unknown = JSON.parse(value || "[]");
    if (!Array.isArray(rows)) return [];
    return rows.filter(row => row && typeof row === "object" && typeof row.imageUrl === "string" && imageAllowed(row.imageUrl)).slice(0, 24).map((row, index) => ({ id: String(row.id || `photo-${index}`), imageUrl: row.imageUrl, caption: String(row.caption || ""), category: galleryCategories.includes(row.category) ? row.category : "Ambience" }));
  } catch { return []; }
}
export function readCafePoint(settings: Record<string, string>): MapPoint | null {
  if (!settings.cafe_latitude?.trim() || !settings.cafe_longitude?.trim()) return null;
  const latitude = Number(settings.cafe_latitude), longitude = Number(settings.cafe_longitude);
  return Number.isFinite(latitude) && Math.abs(latitude) <= 90 && Number.isFinite(longitude) && Math.abs(longitude) <= 180 ? { latitude, longitude, address: settings.cafe_pinned_address || settings.cafe_address || "Café location" } : null;
}
export function branchPoint(branch?: CafeBranch): MapPoint | null {
  return branch && branch.latitude !== null && branch.longitude !== null && Number.isFinite(branch.latitude) && Math.abs(branch.latitude) <= 90 && Number.isFinite(branch.longitude) && Math.abs(branch.longitude) <= 180 ? { latitude: branch.latitude, longitude: branch.longitude, address: branch.pinnedAddress || branch.address || branch.name } : null;
}
export function emptyCafeBranch(id: string): CafeBranch {
  return { id, name: "", address: "", phone: "", email: "", openingHours: "", latitude: null, longitude: null, pinnedAddress: "" };
}
export function readCafeBranches(settings: Record<string, string>): CafeBranch[] {
  if (settings.cafe_branches !== undefined) {
    try {
      const rows: unknown = JSON.parse(settings.cafe_branches);
      const ids = new Set<string>();
      if (Array.isArray(rows)) return rows.filter(row => row && typeof row === "object").slice(0, 20).map((row, index) => {
        let id = String(row.id || `branch-${index}`);
        while (ids.has(id)) id += `-${index}`;
        ids.add(id);
        const branch: CafeBranch = { id, name: String(row.name || ""), address: String(row.address || ""), phone: String(row.phone || ""), email: String(row.email || ""), openingHours: String(row.openingHours || ""), latitude: typeof row.latitude === "number" ? row.latitude : null, longitude: typeof row.longitude === "number" ? row.longitude : null, pinnedAddress: String(row.pinnedAddress || "") };
        if (!branchPoint(branch)) { branch.latitude = null; branch.longitude = null; }
        return branch;
      });
    } catch { /* Retain legacy details if the branch setting cannot be read. */ }
  }
  const point = readCafePoint(settings);
  return [{ id: "main-branch", name: "Main branch", address: settings.cafe_address || "", phone: settings.cafe_phone || "", email: settings.cafe_email || "", openingHours: settings.cafe_opening_hours || "", latitude: point?.latitude ?? null, longitude: point?.longitude ?? null, pinnedAddress: point?.address || "" }];
}
export function descriptionEditorHeight(value?: string): number {
  const height = Number(value);
  return Number.isFinite(height) && height > 0 ? Math.min(1000, Math.max(160, height)) : 240;
}
